import { type MutableRefObject } from 'react';
import { type StateCreator } from 'zustand';

export type CommentInputFocusTarget = 'annotation' | 'panel';
export type CommentInputFocusHandler = () => void;

/**
 * Comment-input focus coordination is imperative (handler registration + a scheduled
 * `requestAnimationFrame`), not render state, so it is backed by refs owned by the provider
 * rather than reactive store state. The slice only exposes stable actions that operate on
 * those refs, keeping the store's declarative surface clean.
 */
export type CommentInputFocusRefs = {
  animationFrameRef: MutableRefObject<number | null>;
  handlersRef: MutableRefObject<Partial<Record<CommentInputFocusTarget, CommentInputFocusHandler>>>;
  pendingTargetRef: MutableRefObject<CommentInputFocusTarget | null>;
};

export type AnnotationCommentInputSlice = {
  /**
   * Cancels any scheduled focus and clears the pending target. Used on clip change and unmount.
   */
  cancelPendingCommentInputFocus: () => void;
  focusCommentInput: (target: CommentInputFocusTarget) => void;
  registerCommentInputFocusHandler: (target: CommentInputFocusTarget, handler: CommentInputFocusHandler) => () => void;
};

export type CreateAnnotationCommentInputSlice = (
  refs: CommentInputFocusRefs,
) => StateCreator<AnnotationCommentInputSlice, [], [], AnnotationCommentInputSlice>;

export const createAnnotationCommentInputSlice: CreateAnnotationCommentInputSlice =
  ({ animationFrameRef, handlersRef, pendingTargetRef }) =>
  () => {
    const scheduleCommentInputFocus = (handler: CommentInputFocusHandler) => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null;
        handler();
      });
    };

    return {
      cancelPendingCommentInputFocus: () => {
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        pendingTargetRef.current = null;
      },
      focusCommentInput: (target) => {
        const handler = handlersRef.current[target];

        if (!handler) {
          pendingTargetRef.current = target;
          return;
        }

        pendingTargetRef.current = null;
        scheduleCommentInputFocus(handler);
      },
      registerCommentInputFocusHandler: (target, handler) => {
        handlersRef.current[target] = handler;

        if (pendingTargetRef.current === target) {
          pendingTargetRef.current = null;
          scheduleCommentInputFocus(handler);
        }

        return () => {
          if (handlersRef.current[target] === handler) {
            delete handlersRef.current[target];
          }
        };
      },
    };
  };
